import { SectionManifest, ViewDSL } from '../types';

// Button Section Example
export const buttonSectionManifest: SectionManifest = {
  id: 'button',
  name: 'Button',
  description: 'Interactive button element with multiple variants for different actions',
  version: '1.0.0',
  category: 'element',
  
  props: {
    children: {
      type: 'ReactNode',
      required: true,
      description: 'Button text or content'
    },
    variant: {
      type: 'string',
      default: 'primary',
      description: 'Visual style variant'
    },
    size: {
      type: 'string',
      default: 'medium',
      description: 'Button size'
    },
    disabled: {
      type: 'boolean',
      default: false,
      description: 'Disable button interaction'
    },
    onClick: {
      type: 'function',
      description: 'Click handler'
    }
  },

  defaultProps: {
    variant: 'primary',
    size: 'medium',
    disabled: false
  },

  schema: {
    type: 'object',
    properties: {
      children: { type: 'string' },
      variant: { 
        type: 'string', 
        enum: ['primary', 'secondary', 'danger', 'ghost'] 
      },
      size: { 
        type: 'string', 
        enum: ['small', 'medium', 'large'] 
      },
      disabled: { type: 'boolean' },
      'aria-label': { type: 'string' },
      role: { type: 'string' },
      tabIndex: { type: 'number' }
    },
    required: ['children']
  },

  tokens: {
    primaryColor: '$colors.primary.500',
    primaryHover: '$colors.primary.600',
    textColor: '$colors.white',
    borderRadius: '$radii.md',
    fontSize: '$fontSizes.sm',
    padding: '$space.3'
  },

  variants: [
    {
      name: 'primary',
      description: 'Primary action button with brand colors',
      props: {
        className: 'bg-primary-500 hover:bg-primary-600 text-white'
      },
      tokens: {
        bgColor: '$colors.primary.500',
        bgHover: '$colors.primary.600',
        textColor: '$colors.white'
      }
    },
    {
      name: 'secondary',
      description: 'Secondary action button with muted colors',
      props: {
        className: 'bg-gray-100 hover:bg-gray-200 text-gray-900'
      },
      tokens: {
        bgColor: '$colors.gray.100',
        bgHover: '$colors.gray.200',
        textColor: '$colors.gray.900'
      }
    },
    {
      name: 'danger',
      description: 'Destructive action button with warning colors',
      props: {
        className: 'bg-red-500 hover:bg-red-600 text-white'
      },
      tokens: {
        bgColor: '$colors.red.500',
        bgHover: '$colors.red.600',
        textColor: '$colors.white'
      }
    },
    {
      name: 'ghost',
      description: 'Minimal button with transparent background',
      props: {
        className: 'bg-transparent hover:bg-gray-100 text-gray-700'
      },
      tokens: {
        bgColor: 'transparent',
        bgHover: '$colors.gray.100',
        textColor: '$colors.gray.700'
      }
    }
  ],

  accessibility: {
    role: 'button',
    ariaLabel: 'Interactive button',
    keyboardSupport: true
  },

  stories: [
    {
      name: 'Default',
      args: { children: 'Click me' }
    },
    {
      name: 'All Variants',
      args: { children: 'Button' }
    },
    {
      name: 'Sizes',
      args: { children: 'Button', size: 'large' }
    },
    {
      name: 'Disabled',
      args: { children: 'Disabled', disabled: true }
    }
  ],

  examples: [
    {
      name: 'Simple Button',
      description: 'Basic button usage in a view',
      dsl: {
        meta: { name: 'Button Example', id: 'button-example', version: '1.0.0' },
        layout: { type: 'stack', gap: '$space.4' },
        sections: [
          {
            use: 'Button',
            variant: 'primary',
            bindings: { children: 'Primary Action' }
          }
        ]
      }
    }
  ]
};

// Card Section Example
export const cardSectionManifest: SectionManifest = {
  id: 'card',
  name: 'Card',
  description: 'Content container with elevated appearance and flexible layout',
  version: '1.0.0',
  category: 'component',
  
  props: {
    children: {
      type: 'ReactNode',
      required: true,
      description: 'Card content'
    },
    title: {
      type: 'string',
      description: 'Card title'
    },
    subtitle: {
      type: 'string', 
      description: 'Card subtitle'
    },
    variant: {
      type: 'string',
      default: 'elevated',
      description: 'Visual style variant'
    },
    padding: {
      type: 'string',
      default: 'medium',
      description: 'Internal padding'
    }
  },

  defaultProps: {
    variant: 'elevated',
    padding: 'medium'
  },

  schema: {
    type: 'object',
    properties: {
      title: { type: 'string' },
      subtitle: { type: 'string' },
      variant: { 
        type: 'string', 
        enum: ['elevated', 'outlined', 'flat'] 
      },
      padding: { 
        type: 'string', 
        enum: ['none', 'small', 'medium', 'large'] 
      },
      'aria-label': { type: 'string' },
      role: { type: 'string' },
      tabIndex: { type: 'number' }
    },
    required: ['children']
  },

  tokens: {
    backgroundColor: '$colors.white',
    borderColor: '$colors.gray.200',
    borderRadius: '$radii.lg',
    shadow: '$shadows.md',
    titleColor: '$colors.gray.900',
    subtitleColor: '$colors.gray.600'
  },

  variants: [
    {
      name: 'elevated',
      description: 'Card with shadow elevation',
      props: {
        className: 'bg-white rounded-lg shadow-md'
      },
      tokens: {
        shadow: '$shadows.md',
        border: 'none'
      }
    },
    {
      name: 'outlined',
      description: 'Card with border outline',
      props: {
        className: 'bg-white rounded-lg border border-gray-200'
      },
      tokens: {
        shadow: 'none',
        border: '$borders.gray.200'
      }
    },
    {
      name: 'flat',
      description: 'Minimal card without elevation',
      props: {
        className: 'bg-gray-50 rounded-lg'
      },
      tokens: {
        shadow: 'none',
        border: 'none',
        backgroundColor: '$colors.gray.50'
      }
    }
  ],

  dependencies: ['Button'], // Cards often contain buttons

  accessibility: {
    role: 'article',
    keyboardSupport: false
  },

  stories: [
    {
      name: 'Default',
      args: { 
        title: 'Card Title',
        children: 'Card content goes here.'
      }
    },
    {
      name: 'With Subtitle',
      args: { 
        title: 'Main Title',
        subtitle: 'Supporting subtitle',
        children: 'Card content with subtitle.'
      }
    },
    {
      name: 'All Variants',
      args: { 
        title: 'Card Title',
        children: 'Content'
      }
    }
  ],

  examples: [
    {
      name: 'Product Card',
      description: 'Card containing product information with button',
      dsl: {
        meta: { name: 'Product Card', id: 'product-card', version: '1.0.0' },
        layout: { type: 'stack', gap: '$space.0' },
        sections: [
          {
            use: 'Card',
            variant: 'elevated',
            bindings: { 
              title: 'Product Name',
              subtitle: '$24.99',
              children: 'Product description and details.'
            },
            grid: { colSpan: 1 }
          },
          {
            use: 'Button',
            variant: 'primary',
            bindings: { children: 'Add to Cart' }
          }
        ]
      }
    }
  ]
};

// Form Input Section Example
export const inputSectionManifest: SectionManifest = {
  id: 'input',
  name: 'Input',
  description: 'Text input field with validation and multiple states',
  version: '1.0.0',
  category: 'element',
  
  props: {
    type: {
      type: 'string',
      default: 'text',
      description: 'Input type'
    },
    placeholder: {
      type: 'string',
      description: 'Placeholder text'
    },
    label: {
      type: 'string',
      description: 'Input label'
    },
    error: {
      type: 'string',
      description: 'Error message'
    },
    disabled: {
      type: 'boolean',
      default: false,
      description: 'Disable input'
    },
    required: {
      type: 'boolean',
      default: false,
      description: 'Required field indicator'
    },
    value: {
      type: 'string',
      description: 'Input value'
    },
    onChange: {
      type: 'function',
      description: 'Change handler'
    }
  },

  defaultProps: {
    type: 'text',
    disabled: false,
    required: false
  },

  schema: {
    type: 'object',
    properties: {
      type: { 
        type: 'string', 
        enum: ['text', 'email', 'password', 'number', 'tel', 'url'] 
      },
      placeholder: { type: 'string' },
      label: { type: 'string' },
      error: { type: 'string' },
      disabled: { type: 'boolean' },
      required: { type: 'boolean' },
      value: { type: 'string' }
    }
  },

  tokens: {
    borderColor: '$colors.gray.300',
    borderFocus: '$colors.blue.500',
    borderError: '$colors.red.500',
    backgroundColor: '$colors.white',
    textColor: '$colors.gray.900',
    placeholderColor: '$colors.gray.500',
    labelColor: '$colors.gray.700'
  },

  variants: [
    {
      name: 'default',
      description: 'Standard input field',
      props: {
        className: 'border border-gray-300 rounded-md px-3 py-2 focus:border-blue-500'
      }
    },
    {
      name: 'error',
      description: 'Input with error state',
      props: {
        className: 'border border-red-500 rounded-md px-3 py-2 focus:border-red-500'
      },
      conditions: [
        {
          prop: 'error',
          operator: 'contains',
          value: ''
        }
      ]
    }
  ],

  accessibility: {
    ariaLabel: 'Text input field',
    keyboardSupport: true
  },

  stories: [
    {
      name: 'Default',
      args: { placeholder: 'Enter text...' }
    },
    {
      name: 'With Label',
      args: { label: 'Full Name', placeholder: 'John Doe' }
    },
    {
      name: 'Error State',
      args: { 
        label: 'Email',
        value: 'invalid-email',
        error: 'Please enter a valid email address'
      }
    },
    {
      name: 'Required Field',
      args: { 
        label: 'Password',
        type: 'password',
        required: true
      }
    }
  ],

  examples: [
    {
      name: 'Login Form',
      description: 'Simple login form with input fields',
      dsl: {
        meta: { name: 'Login Form', id: 'login-form', version: '1.0.0' },
        layout: { type: 'stack', gap: '$space.4' },
        sections: [
          {
            use: 'Input',
            bindings: { 
              label: 'Email',
              type: 'email',
              placeholder: 'your@email.com'
            }
          },
          {
            use: 'Input',
            bindings: { 
              label: 'Password',
              type: 'password',
              placeholder: 'Enter password'
            }
          },
          {
            use: 'Button',
            variant: 'primary',
            bindings: { children: 'Sign In' }
          }
        ]
      }
    }
  ]
};

// Export all manifests for easy reference
export const exampleManifests = {
  button: buttonSectionManifest,
  card: cardSectionManifest,
  input: inputSectionManifest
};

// Helper function to validate a manifest
export function validateManifest(manifest: SectionManifest): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Check required fields
  if (!manifest.id) errors.push('Missing required field: id');
  if (!manifest.name) errors.push('Missing required field: name');
  if (!manifest.version) errors.push('Missing required field: version');
  if (!manifest.category) errors.push('Missing required field: category');

  // Validate category
  if (!['element', 'component', 'section'].includes(manifest.category)) {
    errors.push('Invalid category. Must be: element, component, or section');
  }

  // Check variants
  if (!manifest.variants || manifest.variants.length === 0) {
    errors.push('At least one variant is required');
  }

  // Validate tokens use proper format
  if (manifest.tokens) {
    for (const [key, value] of Object.entries(manifest.tokens)) {
      if (typeof value === 'string' && !value.startsWith('$')) {
        errors.push(`Token "${key}" should start with $ for design token reference`);
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
}